// Per-device binding of a configured Bluetooth printer to a physical local device.
//
// A Bluetooth printer is paired to THIS machine (tablet or desktop), so the
// binding is intentionally stored in localStorage — different devices bind the
// same configured printer to their own local Bluetooth/serial device independently.
//
//   deviceId  — Android: Bluetooth MAC address; Electron: serial COM/device path
//   deviceName — human-friendly label shown in the UI

const KEY = 'tv.printerBindings.v1';

export interface PrinterBinding {
  deviceId: string;
  deviceName: string;
  // ISO timestamp of the last successful test print. Absent = assigned but never
  // verified. A COM/Bluetooth port existing does NOT mean the printer responds,
  // so "verified" is only ever set by an actual successful print.
  verifiedAt?: string;
}

type BindingMap = Record<string, PrinterBinding>;

function readAll(): BindingMap {
  try {
    return JSON.parse(localStorage.getItem(KEY) || '{}') as BindingMap;
  } catch {
    return {};
  }
}

export function getBinding(printerId: string): PrinterBinding | null {
  return readAll()[printerId] || null;
}

export function setBinding(printerId: string, binding: PrinterBinding): void {
  const all = readAll();
  all[printerId] = binding;
  localStorage.setItem(KEY, JSON.stringify(all));
}

export function clearBinding(printerId: string): void {
  const all = readAll();
  delete all[printerId];
  localStorage.setItem(KEY, JSON.stringify(all));
}

/** Mark the current binding as verified (called after a successful test print). */
export function markVerified(printerId: string): PrinterBinding | null {
  const all = readAll();
  if (!all[printerId]) return null;
  all[printerId] = { ...all[printerId], verifiedAt: new Date().toISOString() };
  localStorage.setItem(KEY, JSON.stringify(all));
  return all[printerId];
}
